/**
 * @fileoverview Run history panel component for the legacy migration page.
 * @module system-web/components/migration/RunHistoryPanel
 *
 * Responsibilities:
 * - Render a list of past migration runs (dry-run and apply) for a given dataset + tenant.
 * - Show status badges, counts, timing, and per-run summary.
 *
 * Dependencies:
 * - lucide-react (icons)
 * - useLegacyRunHistory hook
 * - MigrationRunRow from legacyMigrationService
 *
 * Architecture:
 * - Receives data from useLegacyRunHistory (local-first liveQuery on Dexie).
 * - Purely presentational — no direct API calls or Dexie writes.
 *
 * Notes:
 * - Phase 0: panel renders an empty state when no runs exist.
 * - Provisional run rows inserted by startRun() appear immediately via liveQuery.
 *
 * @reusable
 * Usage: Rendered inside LegacyMigrationPage alongside LegacyMigrationGrid.
 * Rules: presentation-only.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, XCircle, Clock, ChevronDown, ChevronUp, Loader2, RefreshCw } from 'lucide-react';
import type { MigrationRunRow } from '../../services/legacyMigrationService';
import {
  formatMigrationFailureForAdmin,
  formatRehostErrorType,
  formatRehostProgressLine,
  resolveFileCopyBadge,
  summarizeFailedRows,
} from '../../services/departmentWideStepper';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract RunHistoryPanelProps
 *
 * Purpose:
 * - Props for the RunHistoryPanel component.
 *
 * Fields:
 * - runs: Migration run rows from useLegacyRunHistory (newest first).
 * - isLoading: True while the initial Dexie query is resolving.
 * - error: Non-null when the subscription fails.
 * - onRefresh: Optional callback to manually re-sync runs and receipts.
 *
 * Rules:
 * - Panel shows empty state when runs is empty and isLoading is false.
 *
 * Sync:
 * - runs are Dexie-local mirrors of system Mongo legacyMigrationRuns.
 */
export interface RunHistoryPanelProps {
  runs: MigrationRunRow[];
  isLoading: boolean;
  isRefreshing?: boolean;
  error: string | null;
  onRefresh?: () => void;
  onRetryFailedFileCopy?: (runId: string) => void;
  retryingRunId?: string | null;
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

const STATUS_CONFIG = {
  pending:     { icon: Clock,          color: '#6b7280', bg: 'rgba(107,114,128,0.1)', label: 'Pending / Queued' },
  running:     { icon: Loader2,        color: '#2563eb', bg: 'rgba(37,99,235,0.1)',   label: 'In Progress'      },
  in_progress: { icon: Loader2,        color: '#2563eb', bg: 'rgba(37,99,235,0.1)',   label: 'In Progress'      },
  completed:   { icon: CheckCircle2,   color: '#16a34a', bg: 'rgba(22,163,74,0.1)',   label: 'Completed'        },
  failed:      { icon: XCircle,        color: '#dc2626', bg: 'rgba(220,38,38,0.1)',   label: 'Failed'           },
  cancelled:   { icon: AlertTriangle,  color: '#d97706', bg: 'rgba(217,119,6,0.1)',   label: 'Cancelled'        },
} as const;

function StatusBadge({ run }: { run: MigrationRunRow }) {
  const copyKind = resolveFileCopyBadge(run);
  if (copyKind === 'copying') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
        <Loader2 className="h-3 w-3 animate-spin" />
        Copying files
      </span>
    );
  }
  if (copyKind === 'not_copied') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
        <AlertTriangle className="h-3 w-3" />
        Metadata only — files not copied
      </span>
    );
  }
  if (copyKind === 'copy_failed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
        <XCircle className="h-3 w-3" />
        Copy failed
      </span>
    );
  }

  if (run.status === 'completed' && run.counts.failed > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
        <AlertTriangle className="h-3 w-3 text-amber-600" />
        Completed ({run.counts.failed} Failed)
      </span>
    );
  }

  const cfg = STATUS_CONFIG[run.status as keyof typeof STATUS_CONFIG] ?? STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <Icon className={`h-3 w-3 ${run.status === 'running' || (run.status as string) === 'in_progress' ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Run row
// ---------------------------------------------------------------------------

function fmtDate(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function RunRow({
  run,
  onRetryFailedFileCopy,
  retryingRunId,
}: {
  run: MigrationRunRow;
  onRetryFailedFileCopy?: (runId: string) => void;
  retryingRunId?: string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const { counts } = run;
  const copyLine = formatRehostProgressLine({ rehostStatus: run.rehostStatus, rehostCounts: run.rehostCounts });
  const canRetryFailedCopies = run.rehostStatus === 'partial_failed'
    && (run.rehostCounts?.failed ?? 0) > 0
    && Boolean(onRetryFailedFileCopy);
  const failedReasons = run.failedReasonSummary?.length
    ? run.failedReasonSummary.map((reason) => ({
      ...reason,
      error: formatMigrationFailureForAdmin(reason.error),
    }))
    : summarizeFailedRows((run.failedRows ?? run.byRow ?? []).filter((row) => !('action' in row) || row.action === 'failed'));

  return (
    <div className="border rounded-lg mb-2 overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="flex-1 flex flex-wrap items-center gap-2">
          <StatusBadge run={run} />
          <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
            {run.mode === 'dry-run' ? 'Dry Run' : 'Apply'}
          </span>
          {copyLine ? (
            <span className="text-xs text-gray-600">
              {copyLine}
            </span>
          ) : null}
          {run.isGlobal || run.datasetKey === 'department-wide' ? (
            <Link
              to={`/legacy-migration/department-runs/${run._id}`}
              onClick={(event) => event.stopPropagation()}
              className="text-xs font-medium px-2 py-0.5 rounded bg-orange-50 text-orange-700 hover:bg-orange-100"
            >
              Department-wide report
            </Link>
          ) : null}
          <span className="text-xs text-gray-500">{fmtDate(run.startedAt)}</span>
        </div>
        <div className="flex items-center gap-4 text-xs tabular-nums opacity-70">
          <span title="Created">{counts.created} created</span>
          <span title="Updated">{counts.updated} updated</span>
          <span title="Skipped">{counts.skipped} skipped</span>
          {counts.failed > 0 && (
            <span className="text-red-600" title="Failed">{counts.failed} failed</span>
          )}
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 opacity-40" /> : <ChevronDown className="h-4 w-4 opacity-40" />}
      </button>

      {expanded && (
        <div className="px-4 pb-3 text-xs border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-gray-600">
            <dt className="font-medium">Run ID</dt>
            <dd className="font-mono truncate">{run._id}</dd>
            <dt className="font-medium">Selected</dt>
            <dd>{counts.selected === 1 && (counts.created + counts.updated + counts.skipped + counts.failed) > 1
              ? `${counts.created + counts.updated + counts.skipped + counts.failed} (all records)`
              : counts.selected}</dd>
            <dt className="font-medium">Finished</dt>
            <dd>{fmtDate(run.finishedAt)}</dd>
          </dl>
          {run.rehostErrorSummary && run.rehostErrorSummary.length > 0 && (
            <div className="mt-3">
              <p className="font-medium text-red-800 mb-1">File copy reasons</p>
              <ul className="space-y-0.5">
                {run.rehostErrorSummary.map((reason) => (
                  <li key={reason.errorType} className="text-red-700 break-words">
                    {reason.count} × {formatRehostErrorType(reason.errorType)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {canRetryFailedCopies && (
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-md text-orange-800 border-orange-200 bg-orange-50 hover:bg-orange-100 disabled:opacity-50"
              disabled={retryingRunId === run._id}
              onClick={(event) => {
                event.stopPropagation();
                onRetryFailedFileCopy?.(run._id);
              }}
            >
              {retryingRunId === run._id ? 'Retrying failed files…' : 'Retry failed files'}
            </button>
          )}
          {failedReasons.length > 0 && (
            <div className="mt-3">
              <p className="font-medium text-amber-800 mb-1">Failed reasons ({counts.failed})</p>
              <ul className="space-y-0.5">
                {failedReasons.slice(0, 8).map((reason) => (
                  <li key={reason.error} className="text-amber-900 break-words">
                    {reason.count} × {reason.error}
                  </li>
                ))}
                {failedReasons.length > 8 && (
                  <li className="text-gray-400">…and {failedReasons.length - 8} more reason groups</li>
                )}
              </ul>
            </div>
          )}
          {run.steps && run.steps.length > 0 && (
            <ol className="mt-3 space-y-1">
              {run.steps.map((step) => (
                <li key={step.adapterKey} className="flex justify-between gap-2 text-gray-600">
                  <span className="min-w-0 truncate">
                    {step.displayName}
                    {step.error ? <span className="text-red-600"> — {formatMigrationFailureForAdmin(step.error)}</span> : null}
                  </span>
                  <span className="tabular-nums shrink-0">
                    {step.status}
                    {step.counts.created || step.counts.updated
                      ? ` · ${step.counts.created} created · ${step.counts.updated} updated`
                      : ''}
                    {step.counts.failed ? ` · ${step.counts.failed} failed` : ''}
                  </span>
                </li>
              ))}
            </ol>
          )}
          {run.errors.length > 0 && (
            <div className="mt-3">
              <p className="font-medium text-red-700 mb-1">Errors ({run.errors.length})</p>
              <ul className="space-y-0.5">
                {(run.errors as string[]).slice(0, 5).map((e, i) => (
                  <li key={i} className="text-red-600 truncate">{String(e)}</li>
                ))}
                {run.errors.length > 5 && (
                  <li className="text-gray-400">…and {run.errors.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component RunHistoryPanel
 *
 * Purpose:
 * - Display a collapsible list of past migration runs from Dexie.
 *
 * Props:
 * - See RunHistoryPanelProps.
 *
 * Behavior:
 * - Shows spinner while isLoading.
 * - Shows empty state when no runs exist.
 * - Renders runs newest-first with expandable detail rows.
 *
 * Data Source:
 * - local (Dexie via useLegacyRunHistory — passed as props)
 *
 * Constraints:
 * - No direct API calls.
 *
 * Performance:
 * - Collapses run detail by default to keep the panel compact.
 *
 * @reusable
 * Usage: Rendered in LegacyMigrationPage below the grid.
 * Rules: presentation-only; stateful (expanded row toggle is local UI state).
 */
export function RunHistoryPanel({
  runs,
  isLoading,
  isRefreshing = false,
  error,
  onRefresh,
  onRetryFailedFileCopy,
  retryingRunId = null,
}: RunHistoryPanelProps) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm opacity-60">
        <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
        <span>Loading run history…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-red-600">
        <XCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div>
      {onRefresh && (
        <div className="flex items-center justify-between mb-3 pt-1">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Migration Runs ({runs.length})
          </span>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border rounded-md hover:bg-gray-50 text-gray-600 font-medium transition-colors disabled:opacity-50"
            title="Refresh run history and receipts from server"
          >
            <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin text-orange-600' : ''}`} />
            <span>{isRefreshing ? 'Refreshing…' : 'Refresh Runs'}</span>
          </button>
        </div>
      )}

      {runs.length === 0 ? (
        <p className="py-4 text-sm opacity-50 text-center">No migration runs yet for this department and dataset.</p>
      ) : (
        runs.map((run) => (
          <RunRow
            key={run._id}
            run={run}
            onRetryFailedFileCopy={onRetryFailedFileCopy}
            retryingRunId={retryingRunId}
          />
        ))
      )}
    </div>
  );
}
